import React, { useEffect, useState, useMemo, useRef } from 'react';
import './App.css';
import axios from 'axios';

import nochillioBadge from './assets/images/nochillio.png';
import nochillBadge from './assets/images/nochill.png';

import CountdownTimer from './components/CountdownTimer';
import AliasFilterDropdown from './components/AliasFilterDropdown';
import SortIcon from './components/SortIcon';

function App() {
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']");
    if (favicon) {
      favicon.href = nochillioBadge;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = nochillioBadge;
      document.head.appendChild(newLink);
    }
  }, []);

  const API_BASE = 'https://api.looksrarenfts.com';

  const [nochillBadges, setNochillBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [aliasFilter, setAliasFilter] = useState([]);
  const [isAliasFilterOpen, setIsAliasFilterOpen] = useState(false);
  const [aliasFilterSearch, setAliasFilterSearch] = useState('');
  const aliasFilterRef = useRef(null);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

  const [showOverrideModal, setShowOverrideModal] = useState(false);

  const [totalUsd, setTotalUsd] = useState(0);
  const [totalAvax, setTotalAvax] = useState(0);

  useEffect(() => {
    fetchNochillBadges();
    fetchPortfolio();
  }, []);

  const fetchNochillBadges = async () => {
    const url = `${API_BASE}/api/nochill-badges`;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(url);
      setNochillBadges(response.data);
    } catch (err) {
      console.error('fetchNochillBadges → ERROR:', err);
      setError('Failed to load leaderboard data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const res = await axios.get('https://nochillio-drop-api-production.up.railway.app/portfolio');
      setTotalUsd(res.data.totalUsd);
      setTotalAvax(res.data.totalAvax);
    } catch (err) {
      console.error('Failed to fetch portfolio totals:', err);
    }
  };

  const uniqueAliases = useMemo(() => {
    const aliases = Array.from(new Set(nochillBadges.map((row) => row.username)));
    return aliases.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [nochillBadges]);

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const toggleAliasFilter = () => setIsAliasFilterOpen((prev) => !prev);
  const handleAliasValueChange = (val) => {
    setAliasFilter((prev) =>
        prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
    );
  };
  const handleAliasFilterReset = () => {
    setAliasFilter([]);
    setAliasFilterSearch('');
  };
  const handleAliasFilterClose = () => setIsAliasFilterOpen(false);

  // Close alias filter dropdown on click outside
  useEffect(() => {
    if (!isAliasFilterOpen) return;
    const handleClickOutside = (e) => {
      if (aliasFilterRef.current && !aliasFilterRef.current.contains(e.target)) {
        setIsAliasFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAliasFilterOpen]);
  const handleSort = (column) => {
    setSortConfig((prev) =>
        prev.column === column
            ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
            : { column, direction: 'asc' }
    );
  };
  const handleOverrideClick = (e) => {
    e.preventDefault();
    setShowOverrideModal(true);
  };
  const handleAgree = () => {
    window.open('https://id.nochill.io/authenticate', '_blank');
    setShowOverrideModal(false);
  };
  const handleCancel = () => {
    setShowOverrideModal(false);
  };

  const filteredBadges = useMemo(() => {
    return nochillBadges.filter((row) => {
      if (searchText && !row.username.toLowerCase().includes(searchText.toLowerCase()))
        return false;
      if (aliasFilter.length > 0 && !aliasFilter.includes(row.username)) return false;
      return true;
    });
  }, [nochillBadges, searchText, aliasFilter]);

  const sortedBadges = useMemo(() => {
    const data = [...filteredBadges];
    if (sortConfig.column) {
      const { column } = sortConfig;
      data.sort((a, b) => {
        const parseVal = (val) =>
            column === 'spectrum' ? parseInt(val, 10) || 0 : parseFloat(val) || 0;
        const aVal = parseVal(a[column]);
        const bVal = parseVal(b[column]);
        return aVal - bVal;
      });
      if (sortConfig.direction === 'desc') data.reverse();
    }
    return data;
  }, [filteredBadges, sortConfig]);

  return (
      <div className="app-container">
        <header className="header">
          <a href="https://nochill.io" target="_blank" rel="noopener noreferrer">
            <img src="https://nochill.io/images/assets/worldorder.png" alt="NOCHILL Logo" />
          </a>
          <a className="override-link" href="#" onClick={handleOverrideClick}>
            LINK WALLET
          </a>
        </header>

        {showOverrideModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <p>The default wallet for scoring on the leaderboard is the dynamic wallet in The Arena App.</p>
                <p>
                  By clicking <strong>I Agree</strong>, you acknowledge that only the linked wallet's
                  assets will count for scoring.
                </p>
                <p>
                  <strong>IMPORTANT:</strong> After connecting, click "Link Wallet to X" to save your
                  entry.
                </p>
                <div className="modal-buttons">
                  <button onClick={handleAgree}>I Agree</button>
                  <button onClick={handleCancel}>Cancel</button>
                </div>
              </div>
            </div>
        )}

        <div className="table-container">
          <section className="section">
            <div className="rewards-header">

              <div>
                LEADERBOARD DISTRIBUTIONS: ${totalUsd.toLocaleString()} / {totalAvax.toLocaleString(undefined, { maximumFractionDigits: 2 })} AVAX
              </div>
              <div className="next-refresh-text">
                NEXT UPDATE: <CountdownTimer />
              </div>
            </div>

            <div className="table-response-wrapper">
              <table className="custom-table">
                <thead>
                <tr>
                  <th colSpan="7">
                    <div className="table-search-container">
                      <input
                          type="text"
                          placeholder="Search by username..."
                          value={searchText}
                          onChange={handleSearchChange}
                          className="search-input"
                      />
                    </div>
                  </th>
                </tr>
                <tr>
                  <th style={{ width: '60px' }}>
                    RANK
                    <SortIcon
                        onClick={() => handleSort('rank')}
                        isActive={sortConfig.column === 'rank'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th ref={aliasFilterRef}>
                    ALIAS
                    <span className="filter-icon" onClick={toggleAliasFilter}>
                      {isAliasFilterOpen ? '▲' : '▼'}
                    </span>
                    {isAliasFilterOpen && (
                        <AliasFilterDropdown
                            allValues={uniqueAliases}
                            selectedValues={aliasFilter}
                            filterText={aliasFilterSearch}
                            onFilterTextChange={setAliasFilterSearch}
                            onValueChange={handleAliasValueChange}
                            onReset={handleAliasFilterReset}
                            onClose={handleAliasFilterClose}
                        />
                    )}
                  </th>
                  <th>
                    WIDTH
                    <SortIcon
                        onClick={() => handleSort('total_points')}
                        isActive={sortConfig.column === 'total_points'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    NOCHILL POINTS
                    <SortIcon
                        onClick={() => handleSort('nochill')}
                        isActive={sortConfig.column === 'nochill'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    NOCHILLIO POINTS
                    <SortIcon
                        onClick={() => handleSort('nochillio')}
                        isActive={sortConfig.column === 'nochillio'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    SPECTRUM POINTS
                    <SortIcon
                        onClick={() => handleSort('spectrum')}
                        isActive={sortConfig.column === 'spectrum'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    REDACTED
                    <SortIcon
                        onClick={() => handleSort('redacted')}
                        isActive={sortConfig.column === 'redacted'}
                        direction={sortConfig.direction}
                    />
                  </th>
                </tr>
                </thead>
                <tbody>
                {loading && (
                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                )}
                {error && (
                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#ff6b6b' }}>{error}</td></tr>
                )}
                {!loading && !error && sortedBadges.length === 0 && (
                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>No results found.</td></tr>
                )}
                {sortedBadges.map((row) => {
                  const formattedRank = row.rank != null ? row.rank.toLocaleString() : '???';
                  const formatFloat = (val, decimals = 2) =>
                      parseFloat(val).toLocaleString(undefined, {
                        minimumFractionDigits: Number.isInteger(parseFloat(val)) ? 0 : decimals,
                        maximumFractionDigits: decimals,
                      });

                  const badgeSrc =
                      row.badge === 'nochill'
                          ? nochillBadge
                          : row.badge === 'nochillio'
                              ? nochillioBadge
                              : null;

                  return (
                      <tr key={row.username}>
                        <td className="numeric-cell">{formattedRank}</td>
                        <td>
                          <div className="alias-wrapper">
                            {row.twitter_pfp_url && (
                                <img
                                    src={row.twitter_pfp_url}
                                    alt="Twitter PFP"
                                    className="alias-pfp"
                                />
                            )}
                            <div className="alias-text">
                              {row.twitter_username && (
                                  <div className="alias-twitter">
                                    <a
                                        href={`https://x.com/${row.username.replace(/^@/, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="alias-twitter-link"
                                    >
                                      {row.twitter_username}
                                    </a>
                                  </div>
                              )}
                              <div className="alias-row">
                                <a
                                    href={`https://arena.social/${row.username}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="alias-username"
                                >
                                  @{row.username}
                                </a>
                                {badgeSrc && (
                                    <img
                                        src={badgeSrc}
                                        alt={`${row.badge} badge`}
                                        className="badge-icon-inline"
                                    />
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="numeric-cell">{formatFloat(row.total_points, 0)}</td>
                        <td className="numeric-cell">{formatFloat(row.nochill)}</td>
                        <td className="numeric-cell">{formatFloat(row.nochillio)}</td>
                        <td className="numeric-cell">{formatFloat(row.spectrum, 0)}</td>
                        <td className="numeric-cell">{formatFloat(row.redacted)}</td>
                      </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
  );
}

export default App;
